// Lightweight i18n implementation. No external dependency — just a Context
// + a useTranslation hook + a fetch from /api/i18n/{locale} when the user
// switches. Keys are the English source string itself, so a missing
// translation gracefully falls back to English without showing the key.
//
// Usage:
//   const { t, locale, setLocale } = useTranslation();
//   <h1>{t('Sales Invoice')}</h1>
//
// English strings are inline in components — they're the canonical source
// and the fallback. Other locales come from /api/i18n/{locale}.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../App';

const I18nContext = createContext(null);

const STORAGE_KEY = 'erp_locale';
const SUPPORTED = ['en', 'fr', 'ha', 'yo', 'ig', 'ar', 'es', 'pt'];
const LOCALE_LABELS = {
  en: 'English',
  fr: 'Français',
  ha: 'Hausa',
  yo: 'Yorùbá',
  ig: 'Igbo',
  ar: 'العربية',
  es: 'Español',
  pt: 'Português',
};

function readInitialLocale() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED.includes(v)) return v;
  } catch { /* SSR / private mode */ }
  return 'en';
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(readInitialLocale);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch translations whenever the locale changes (skip when 'en' — keys ARE English).
  useEffect(() => {
    if (locale === 'en') {
      setTranslations({});
      return;
    }
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/i18n/${locale}`)
      .then(r => { if (!cancel) setTranslations(r.data?.translations || {}); })
      .catch(() => { if (!cancel) setTranslations({}); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setLocaleState(next);
    // Persist server-side too — best-effort.
    axios.post(`${API}/i18n/me`, { locale: next }).catch(() => {});
  }, []);

  const t = useCallback((key, vars) => {
    let out = translations[key] || key;
    if (vars && typeof out === 'string') {
      // Simple {placeholder} substitution.
      Object.entries(vars).forEach(([k, v]) => {
        out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return out;
  }, [translations]);

  const value = useMemo(() => ({ t, locale, setLocale, loading, supported: SUPPORTED, labels: LOCALE_LABELS }),
                        [t, locale, setLocale, loading]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback so components can be unit-tested without a Provider.
    return { t: (k) => k, locale: 'en', setLocale: () => {}, loading: false,
             supported: SUPPORTED, labels: LOCALE_LABELS };
  }
  return ctx;
}
